import React from 'react';

// App-wide error boundary. Without one, any uncaught error thrown while React
// renders unmounts the whole tree and leaves a blank white page with nothing
// on screen and only a console stack — exactly the failure mode that hid the
// Results junction-matrix crash (Aug 2026). This catches that render error,
// keeps the page visible, and shows the actual message plus a one-click
// "Copy error details" so it can be reported without opening DevTools.
//
// Error boundaries MUST be class components — there is no hook equivalent for
// getDerivedStateFromError / componentDidCatch.

const PU = '#3D1A6E', PU2 = '#7B4DBF', PU_LINE = '#EDE5F8', MUT = '#6B6880', CREAM = '#FBF9F4';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the component stack so the copied report points at the failing
    // component, and log it so it's still in the console for deeper digging.
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('Caught by ErrorBoundary:', error, info);
  }

  handleCopy = () => {
    const { error, info } = this.state;
    const detail = [
      error && (error.stack || error.message || String(error)),
      info && info.componentStack,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
    ].filter(Boolean).join('\n\n');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(detail).then(
        () => this.setState({ copied: true }),
        () => {}
      );
    }
  };

  handleReload = () => window.location.reload();

  render() {
    const { error, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{
        minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 24, fontFamily: 'Nunito Sans, Avenir, sans-serif',
      }}>
        <div style={{
          background: '#fff', border: `1.5px solid ${PU_LINE}`, borderRadius: 14,
          padding: '30px 32px', maxWidth: 560, width: '100%',
          boxShadow: '0 8px 30px rgba(61,26,110,0.10)',
        }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>⚠️</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: PU, marginBottom: 8 }}>
            Something went wrong
          </div>
          <div style={{ fontSize: 14, color: MUT, lineHeight: 1.6, marginBottom: 18 }}>
            This part of the app hit an unexpected error. Your saved work isn't affected —
            reloading usually clears it. If it keeps happening, copy the details below and
            send them over so it can be fixed.
          </div>

          <div style={{
            background: CREAM, border: `1px solid ${PU_LINE}`, borderRadius: 8,
            padding: '10px 12px', fontFamily: 'ui-monospace, monospace', fontSize: 12,
            color: '#993C1D', marginBottom: 18, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: 160, overflowY: 'auto',
          }}>
            {error.message || String(error)}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={this.handleReload} style={{
              background: PU, color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.02em',
            }}>
              ↻ Reload the app
            </button>
            <button onClick={this.handleCopy} style={{
              background: '#fff', color: PU, border: `1.5px solid ${PU2}`, borderRadius: 8,
              padding: '10px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
              {copied ? '✓ Copied' : '⧉ Copy error details'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
